import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { finalize, take, takeUntil } from 'rxjs';

import { CardSetTransformationService, CoreService, DataService, DialogService, FormNavigationService, SaveDataRequest, SpinnerService } from 'dspx-core';

import { POP_UP_CODES, SAVE_RESULT_STATUS, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { ResponseParserService } from '../../services/response-parser.service';

import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class UserComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  id = '';
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getUserConfigWithEmptyResponse;
  templateId: number = TEMPLATES.user;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private route: ActivatedRoute,
    private coreService: CoreService,
    private errorService: ErrorService,
    private dataService: DataService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        this.id = id;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getUserConfigAndResponse;
        this.editing = true;
      }
      else {
        this.resetDirtyFlagAfterLoading();
      }
    });

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();
    this.coreService.conditionFunctions['isReadOnly'] = () => true;

    this.waitForServiceWorker();
  }

  ngOnDestroy(): void {
    this.completeDestroy();
  }

  formLoaded(event: any): void {
    this.templateId = event.template;
    this.versionNo = event.versionNumber;
    this.isFormEditable = !!event?.isFormEditable;

    this.setPreviousModel({ isAsync: true });
  }

  save(event: any): void {
    const saveDataRequest: SaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: false
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showPopup(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any): void {
    this.navigateBackToDashboard();
  }

  dialogButton_saveSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.user.list], ignoreUnsavedChanges);
  }

  private saveData(request: SaveDataRequest): void {
    this.spinnerService.showSpinner();
    this.dataService.saveData(API_ENDPOINTS.user.save, request)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (data?.status === SAVE_RESULT_STATUS.common.success) {
          if (SERVER_ERROR_POP_UP_CODES.saveUser.includes(data.message)) {
            this.activeDialog = data.message;
          }
          else {
            this.id = data.id;
            this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
            this.activeDialog = POP_UP_CODES.common.saveSuccessful;
          }
          this.dialogService.showPopup(this.activeDialog);
        }
      });
  }
}
